import { useEffect, useRef, useState } from "react";
import { Link, useLocation } from "react-router";
import { navigation, portfolio } from "../data/portfolio";
import { useExperience } from "../hooks/useExperience";
import { Arrow, Icon } from "./Icons";
import Logo from "./Logo";
import ThemeToggle from "./ThemeToggle";

export default function Navigation() {
  const location = useLocation();
  const { reducedMotion, paused } = useExperience();
  const [active, setActive] = useState("");
  const [light, setLight] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const [closing, setClosing] = useState(false);
  const header = useRef<HTMLElement>(null);
  const menu = useRef<HTMLDialogElement>(null);
  const trigger = useRef<HTMLButtonElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | undefined>(
    undefined,
  );
  const finishClose = (restoreFocus = true) => {
    clearTimeout(closeTimer.current);
    menu.current?.close();
    setOpen(false);
    setClosing(false);
    if (restoreFocus) trigger.current?.focus({ preventScroll: true });
  };
  const close = () => {
    if (reducedMotion || paused) return finishClose();
    setClosing(true);
    clearTimeout(closeTimer.current);
    closeTimer.current = setTimeout(() => finishClose(), 220);
  };
  useEffect(() => {
    finishClose(false);
    let frame = 0;
    const update = () => {
      frame = 0;
      setScrolled(scrollY > 20);
      const headerHeight = header.current?.offsetHeight ?? 80;
      const sections = [
        ...document.querySelectorAll<HTMLElement>("main > section[id]"),
      ];
      const current = sections
        .filter(
          (section) =>
            section.getBoundingClientRect().top <= headerHeight + 120,
        )
        .at(-1);
      setActive(
        location.pathname === "/"
          ? (current?.id ?? "home")
          : location.pathname.split("/")[1],
      );
      const editorial = document
        .querySelector(".light-section")
        ?.getBoundingClientRect();
      setLight(
        Boolean(
          editorial &&
          editorial.top <= headerHeight + 12 &&
          editorial.bottom > headerHeight,
        ),
      );
      const distance = document.documentElement.scrollHeight - innerHeight;
      header.current?.style.setProperty(
        "--page-progress",
        String(distance > 0 ? Math.min(1, scrollY / distance) : 0),
      );
    };
    const schedule = () => {
      if (!frame) frame = requestAnimationFrame(update);
    };
    update();
    window.addEventListener("scroll", schedule, { passive: true });
    window.addEventListener("resize", schedule);
    const observer = new ResizeObserver(schedule);
    if (document.getElementById("main"))
      observer.observe(document.getElementById("main")!);
    return () => {
      cancelAnimationFrame(frame);
      clearTimeout(closeTimer.current);
      window.removeEventListener("scroll", schedule);
      window.removeEventListener("resize", schedule);
      observer.disconnect();
    };
  }, [location.pathname, location.hash]);
  useEffect(() => {
    if (!open) return;
    const previous = document.body.style.overflow;
    document.body.style.overflow = "hidden";
    return () => {
      document.body.style.overflow = previous;
    };
  }, [open]);
  useEffect(() => {
    const breakpoint = matchMedia("(min-width: 801px)");
    const change = () => {
      if (breakpoint.matches) finishClose(false);
    };
    breakpoint.addEventListener("change", change);
    return () => breakpoint.removeEventListener("change", change);
  }, []);
  const items = (mobile = false) =>
    navigation.map((item, index) => (
      <Link
        key={item.label}
        to={item.to}
        onClick={() => {
          if (mobile && item.to === location.pathname + location.hash) close();
        }}
        aria-current={
          active === item.section
            ? location.pathname === item.to
              ? "page"
              : "location"
            : undefined
        }
      >
        {mobile && (
          <span className="menu-index mono" aria-hidden="true">
            0{index + 1}
          </span>
        )}
        <span>{item.label}</span>
        {mobile && <Arrow diagonal />}
      </Link>
    ));
  return (
    <>
      <a className="skip-link" href="#main">
        Skip to content
      </a>
      <header
        ref={header}
        className="site-header"
        data-theme={light ? "light" : "dark"}
        data-scrolled={scrolled}
      >
        <div className="header-inner container">
          <Link className="wordmark" to="/" aria-label="Darren | home">
            <Logo />
          </Link>
          <nav className="desktop-nav" aria-label="Main navigation">
            {items()}
          </nav>
          <div className="header-actions">
            <ThemeToggle />
            <Link className="nav-contact" to="/contact">
              Let’s talk <Arrow diagonal />
            </Link>
            <button
              ref={trigger}
              className="menu-toggle"
              aria-label="Menu"
              aria-expanded={open}
              aria-controls="mobile-menu"
              onClick={() => {
                clearTimeout(closeTimer.current);
                setClosing(false);
                menu.current?.showModal();
                setOpen(true);
              }}
            >
              <span className="menu-bars" aria-hidden="true">
                <i />
                <i />
              </span>
            </button>
          </div>
        </div>
        <div className="header-progress" aria-hidden="true" />
      </header>
      <dialog
        id="mobile-menu"
        className="mobile-menu"
        ref={menu}
        aria-label="Navigation menu"
        data-closing={closing}
        onCancel={(event) => {
          event.preventDefault();
          close();
        }}
        onClose={() => {
          setOpen(false);
          setClosing(false);
        }}
        onKeyDown={(event) => {
          if (event.key !== "Tab") return;
          const focusable =
            menu.current?.querySelectorAll<HTMLElement>("a, button");
          if (!focusable?.length) return;
          const first = focusable[0],
            last = focusable[focusable.length - 1];
          if (event.shiftKey && document.activeElement === first) {
            event.preventDefault();
            last.focus();
          } else if (!event.shiftKey && document.activeElement === last) {
            event.preventDefault();
            first.focus();
          }
        }}
      >
        <div className="menu-top">
          <span className="wordmark">
            <Logo />
          </span>
          <button onClick={close} autoFocus aria-label="Close">
            <span>Close</span>
            <span className="close-icon" aria-hidden="true">
              <Icon name="close" />
            </span>
          </button>
        </div>
        <div className="menu-heading mono">
          The performance lab <span>Explore the site</span>
        </div>
        <nav aria-label="Mobile navigation">
          {items(true)}
          <Link to="/contact">
            <span className="menu-index mono" aria-hidden="true">
              {String(navigation.length + 1).padStart(2, "0")}
            </span>
            <span>Let’s talk</span>
            <Arrow diagonal />
          </Link>
        </nav>
        <div className="menu-bottom">
          <p className="mono">
            {portfolio.location}
            <br />
            Open to work and new projects.
          </p>
        </div>
      </dialog>
    </>
  );
}
